import React, { useState } from "react";
import { EuiText, EuiTitle, EuiIcon } from "@elastic/eui";
import { ArrowRight } from "lucide-react";
import { AutonubilHomeFeature } from "./features";
import { OpsanioIcon } from "src/assets";
import { useAnketaI18n } from "src/internationalization";
import { buildContactUrl } from "./contactForm";
import { useNavigate } from "react-router";

const C = {
  accent: "var(--ak-accent)",
  accentLight: "var(--ak-accent-tint)",
  accentBorder: "var(--ak-accent-border)",
  pageBg: "var(--ak-page-bg)",
  cardBg: "var(--ak-surface)",
  subtleBg: "var(--ak-surface-2)",
  border: "var(--ak-border)",
  textPrimary: "var(--ak-text)",
  textSecondary: "var(--ak-text-secondary)",
  textMuted: "var(--ak-text-muted)",
  sans: "system-ui,-apple-system,sans-serif",
  radius: 12,
};

interface ServiceCardProps {
  service: AutonubilHomeFeature;
  contactLabel: string;
  onContact: () => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  contactLabel,
  onContact,
}) => {
  const [btnHovered, setBtnHovered] = useState(false);

  // Only the contact button is clickable, so only it reacts to hover - a
  // card-level hover would imply the whole card is a link.
  return (
    <div
      id={`service-${service.id}`}
      style={{
        background: C.cardBg,
        border: `1px solid ${C.border}`,
        borderRadius: C.radius,
        padding: "28px 24px 22px",
        boxShadow: "0 1px 4px var(--ak-shadow)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          background: C.accentLight,
          border: `1px solid ${C.accentBorder}`,
          borderRadius: C.radius,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 18,
          flexShrink: 0,
        }}
      >
        <EuiIcon
          type={service.icon}
          size="xl"
          color="primary"
          style={{ maxHeight: 34, maxWidth: 34 }}
        />
      </div>

      <EuiTitle size="xs">
        <h3
          style={{
            color: C.textPrimary,
            fontWeight: 700,
            marginBottom: 10,
            fontSize: "0.97rem",
          }}
        >
          {service.title}
        </h3>
      </EuiTitle>

      <EuiText size="s" color="subdued">
        <p style={{ lineHeight: 1.65, margin: 0, flex: 1 }}>
          {service.description}
        </p>
      </EuiText>

      <div
        style={{
          height: 1,
          background: C.border,
          margin: "20px 0 14px",
        }}
      />

      <button
        onMouseEnter={() => setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
        onClick={onContact}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          color: btnHovered ? C.accent : C.textMuted,
          fontSize: "0.83rem",
          fontWeight: 600,
          cursor: "pointer",
          padding: 0,
          fontFamily: C.sans,
          transition: "color 0.18s",
        }}
      >
        {contactLabel}
        <ArrowRight
          size={14}
          style={{
            transform: btnHovered ? "translateX(3px)" : "none",
            transition: "transform 0.18s",
          }}
        />
      </button>
    </div>
  );
};

export const AutonubilHomeServcies = (): JSX.Element => {
  const { translate } = useAnketaI18n();
  const navigate = useNavigate();
  const i18nPrefix = "anketa.autonubil.home.services";

  const services: Array<AutonubilHomeFeature> = [
    {
      id: "opsanio",
      title: translate(`${i18nPrefix}.opsanio.title`),
      icon: OpsanioIcon,
      description: translate(`${i18nPrefix}.opsanio.description`),
    },
    {
      id: "kubernetes",
      title: translate(`${i18nPrefix}.kubernetes.title`),
      icon: "logoKubernetes",
      description: translate(`${i18nPrefix}.kubernetes.description`),
    },
    {
      id: "docker",
      title: translate(`${i18nPrefix}.docker.title`),
      icon: "logoDocker",
      description: translate(`${i18nPrefix}.docker.description`),
    },
    {
      id: "devops",
      title: translate(`${i18nPrefix}.devops.title`),
      icon: "logoAppSearch",
      description: translate(`${i18nPrefix}.devops.description`),
    },
    {
      id: "nextcloud",
      title: translate(`${i18nPrefix}.nextcloud.title`),
      icon: "https://upload.wikimedia.org/wikipedia/commons/6/60/Nextcloud_Logo.svg",
      description: translate(`${i18nPrefix}.nextcloud.description`),
    },
    {
      id: "keycloak",
      title: translate(`${i18nPrefix}.keycloak.title`),
      icon: "https://www.keycloak.org/resources/images/logo.svg",
      description: translate(`${i18nPrefix}.keycloak.description`),
    },
    /*     {
      id: "opennebula",
      title: translate(`${i18nPrefix}.migration.title`),
      icon: "https://opennebula.io/wp-content/uploads/2020/04/opennebula_cloud_logo_white_bg.svg",
      description: translate(`${i18nPrefix}.migration.description`),
    }, */
    {
      id: "automation",
      title: translate(`${i18nPrefix}.automation.title`),
      icon: "console",
      description: translate(`${i18nPrefix}.automation.description`),
    },
  ];

  const handleContact = (service: AutonubilHomeFeature) => {
    navigate(
      buildContactUrl({
        topic: service.id,
        message: translate("anketa.autonubil.contact.request", {
          values: { topic: service.id },
        }),
      }),
    );
  };

  return (
    <div
      id="services"
      style={{
        background: C.pageBg,
        padding: "72px clamp(24px, 5vw, 48px)",
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ marginBottom: 48 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: C.accent,
              marginBottom: 10,
            }}
          >
            {translate(`${i18nPrefix}.eyebrow`, {
              defaultMessage: "What we offer",
            } as any)}
          </div>

          <div style={{ width: "fit-content", maxWidth: "100%" }}>
            <EuiTitle size="l">
              <h2
                style={{
                  color: C.textPrimary,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  marginBottom: 0,
                }}
              >
                {translate(`${i18nPrefix}.title`)}
              </h2>
            </EuiTitle>
            <div
              style={{
                width: "100%",
                height: 3,
                marginTop: 14,
                background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
                borderRadius: 2,
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 16,
          }}
        >
          {services.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              contactLabel={translate(`${i18nPrefix}.contact`)}
              onContact={() => handleContact(service)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AutonubilHomeServcies;
